import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/providers';
import { toast } from 'sonner';
import { Mail, RefreshCw, CheckCircle } from 'lucide-react';
import { PostAuthNavbar } from '@/components/navigation/PostAuthNavbar';
import { Footer } from '@/components/marketing/Footer';

export function EmailVerificationRequired() {
    const { user, resendVerificationEmail, logout, refreshUser } = useAuth();
    const [isResending, setIsResending] = useState(false);
    const [lastSentAt, setLastSentAt] = useState<Date | null>(null);
    const [isCheckingVerification, setIsCheckingVerification] = useState(false);

    const handleResendEmail = async () => {
        if (!user) return;

        setIsResending(true);
        try {
            await resendVerificationEmail();
            setLastSentAt(new Date());
            toast.success('Verification email sent! Please check your inbox.');
        } catch (error) {
            toast.error('Failed to send verification email. Please try again.');
        } finally {
            setIsResending(false);
        }
    };

    const handleCheckVerification = async () => {
        if (isCheckingVerification) return;

        setIsCheckingVerification(true);
        try {
            await refreshUser();
        } catch (error) {
            toast.error('Failed to check verification status.');
        } finally {
            setIsCheckingVerification(false);
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
            toast.success('Logged out successfully');
        } catch (error) {
            toast.error('Logout failed');
        }
    };

    const canResend = !lastSentAt || Date.now() - lastSentAt.getTime() > 60000; // 1 minute cooldown

    return (
        <div className="min-h-screen flex flex-col">
            <PostAuthNavbar />
            <main className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md space-y-6">
                    <div className="text-center space-y-2">
                        <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                            <Mail className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h1 className="text-2xl font-bold">Verify your email</h1>
                        <p className="text-muted-foreground">
                            We've sent a verification link to{' '}
                            <span className="font-medium text-foreground">{user?.email}</span>
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div className="p-4 bg-muted rounded-lg space-y-3">
                            <h3 className="font-medium">Next steps:</h3>
                            <ol className="space-y-2 text-sm text-muted-foreground">
                                <li className="flex items-start gap-2">
                                    <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">
                                        1
                                    </span>
                                    Check your email inbox (and spam folder)
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">
                                        2
                                    </span>
                                    Click the verification link in the email
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">
                                        3
                                    </span>
                                    Return here and click "I've verified my email"
                                </li>
                            </ol>
                        </div>

                        <div className="space-y-3">
                            <Button
                                onClick={handleCheckVerification}
                                disabled={isCheckingVerification}
                                className="w-full"
                                variant="default"
                            >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                {isCheckingVerification ? 'Checking...' : 'I\'ve verified my email'}
                            </Button>

                            <Button
                                onClick={handleResendEmail}
                                disabled={isResending || !canResend}
                                variant="outline"
                                className="w-full"
                            >
                                <RefreshCw className={`w-4 h-4 mr-2 ${isResending ? 'animate-spin' : ''}`} />
                                {isResending ? 'Sending...' : 'Resend verification email'}
                            </Button>

                            {lastSentAt && !canResend && (
                                <p className="text-xs text-muted-foreground text-center">
                                    Email sent! You can request another in{' '}
                                    {Math.ceil((60000 - (Date.now() - lastSentAt.getTime())) / 1000)}s
                                </p>
                            )}
                        </div>

                        <div className="pt-4 border-t">
                            <Button
                                onClick={handleLogout}
                                variant="ghost"
                                size="sm"
                                className="w-full text-muted-foreground"
                            >
                                Use a different account
                            </Button>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}
